import {
  Sparkles,
  PhoneCall,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  ArrowDown,
  Minus,
  ArrowUp,
  Flame,
  Facebook,
  Instagram,
  MessageCircle,
  Globe,
  ShoppingBag,
  Store,
  type LucideIcon,
} from 'lucide-react';

export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'converted' | 'closed';
export type LeadPriority = 'low' | 'medium' | 'high' | 'urgent';

interface BadgeMeta {
  label: string;
  icon: LucideIcon;
  /** Token-based bg/text/border classes — merged onto the shared Badge primitive via className. */
  className: string;
}

/**
 * Status vocabulary. `Lead['status']` (see src/types/api.ts) is
 * 'new' | 'contacted' | 'qualified' | 'converted' | 'closed' — note there is
 * no 'lost' status in the data model (the previous UI's filter list included
 * one that could never match any real lead).
 */
export const STATUS_META: Record<LeadStatus, BadgeMeta> = {
  new: { label: 'New', icon: Sparkles, className: 'bg-info/10 text-info border-info/20' },
  contacted: { label: 'Contacted', icon: PhoneCall, className: 'bg-muted text-muted-foreground border-border' },
  qualified: { label: 'Qualified', icon: ShieldCheck, className: 'bg-warning/10 text-warning border-warning/20' },
  converted: { label: 'Converted', icon: CheckCircle2, className: 'bg-success/10 text-success border-success/20' },
  closed: { label: 'Closed', icon: XCircle, className: 'bg-slate-100 text-slate-500 border-slate-200' },
};

export const PRIORITY_META: Record<LeadPriority, BadgeMeta> = {
  low: { label: 'Low', icon: ArrowDown, className: 'bg-muted text-muted-foreground border-border' },
  medium: { label: 'Medium', icon: Minus, className: 'bg-info/10 text-info border-info/20' },
  high: { label: 'High', icon: ArrowUp, className: 'bg-warning/10 text-warning border-warning/20' },
  urgent: { label: 'Urgent', icon: Flame, className: 'bg-destructive/10 text-destructive border-destructive/20' },
};

interface PlatformMeta {
  label: string;
  icon: LucideIcon;
  className: string;
}

const PLATFORM_META: Record<string, PlatformMeta> = {
  facebook: { label: 'Facebook', icon: Facebook, className: 'bg-channel-facebook/10 text-channel-facebook' },
  instagram: { label: 'Instagram', icon: Instagram, className: 'bg-channel-instagram/10 text-channel-instagram' },
  whatsapp: { label: 'WhatsApp', icon: MessageCircle, className: 'bg-channel-whatsapp/10 text-channel-whatsapp' },
  website: { label: 'Website', icon: Globe, className: 'bg-channel-website/10 text-channel-website' },
  shopify: { label: 'Shopify', icon: ShoppingBag, className: 'bg-channel-shopify/10 text-channel-shopify' },
  woocommerce: { label: 'WooCommerce', icon: Store, className: 'bg-channel-woocommerce/10 text-channel-woocommerce' },
};

const DEFAULT_PLATFORM_META: PlatformMeta = {
  label: 'Other',
  icon: Globe,
  className: 'bg-muted text-muted-foreground',
};

export function getPlatformMeta(platform?: string): PlatformMeta {
  if (!platform) return DEFAULT_PLATFORM_META;
  return PLATFORM_META[platform.toLowerCase()] || DEFAULT_PLATFORM_META;
}

/** "instagram_dm" -> "Instagram Dm", "facebook_page" -> "Facebook Page" */
export function formatSource(source?: string): string {
  if (!source) return '—';
  return source
    .split(/[_-]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function getInitials(name?: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const initials = parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
  return initials || '?';
}
